import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Star, ChefHat, ChevronLeft, ChevronRight } from 'lucide-react';
import { getImageUrl } from '../../../services/api';
import { getCateringServiceTypeLabel, getOfferStartingPrice } from '../../../constants/catering';
import namkeFallback from '../../../assets/namke-fallback.png';

const formatPrice = (amount) => {
  const value = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
  if (Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value);
};

const CateringServicesCarousel = ({ services = [], loading, error = null }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const trackRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateArrows = () => {
    const el = trackRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      ro.disconnect();
    };
  }, [services]);

  const scroll = (direction) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * 320, behavior: 'smooth' });
  };

  return (
    <section style={{ maxWidth: '1320px', margin: '0 auto', padding: 'clamp(48px, 6vw, 88px) clamp(16px, 3vw, 40px)' }}>
      <div style={{ background: '#054625', color: '#FBF9D9', borderRadius: 'clamp(28px, 4vw, 44px)', padding: 'clamp(32px, 5vw, 64px)', display: 'flex', flexDirection: 'column', gap: 'clamp(24px, 4vw, 40px)' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '18px', alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', alignItems: 'flex-start' }}>
            <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#FFC717' }}>{t('catering.hero.badge')}</span>
            <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(26px, 3.2vw, 42px)', lineHeight: 1.05, letterSpacing: '-0.02em', maxWidth: '22ch' }}>{t('catering.hero.title')}</h2>
            <p style={{ margin: 0, maxWidth: '46ch', fontSize: '15.5px', lineHeight: 1.6, color: 'rgba(251,249,217,0.8)' }}>{t('catering.hero.carouselSubtitle')}</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/traiteur')}
            style={{ flex: 'none', border: 'none', cursor: 'pointer', background: '#EF4C23', color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '15.5px', padding: '15px 32px', borderRadius: '999px' }}
          >
            {t('home.seeAll')}
          </button>
        </div>

        {error && (
          <div style={{ borderRadius: '20px', background: 'rgba(239,76,35,0.14)', border: '1px solid rgba(239,76,35,0.3)', padding: '16px', fontSize: '14px', color: '#FBF9D9' }}>
            {error}
          </div>
        )}

        {!loading && (!services || services.length === 0) && !error ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <ChefHat className="h-10 w-10 mx-auto mb-3" style={{ color: 'rgba(251,249,217,0.4)' }} />
            <p style={{ fontSize: '14px', color: 'rgba(251,249,217,0.7)' }}>{t('catering.noServicesAvailable')}</p>
          </div>
        ) : (
        <div style={{ position: 'relative' }}>
          <div
            className="absolute left-0 top-0 bottom-0 w-10 sm:w-16 z-10 pointer-events-none"
            style={{ background: 'linear-gradient(to right, #054625, transparent)', opacity: canScrollLeft ? 1 : 0, transition: 'opacity 0.2s' }}
          />
          <div
            className="absolute right-0 top-0 bottom-0 w-10 sm:w-16 z-10 pointer-events-none"
            style={{ background: 'linear-gradient(to left, #054625, transparent)', opacity: canScrollRight ? 1 : 0, transition: 'opacity 0.2s' }}
          />

          <div
            ref={trackRef}
            onScroll={updateArrows}
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {loading
              ? Array.from({ length: 4 }).map((_, index) => (
                  <div key={`skeleton-${index}`} className="min-w-[16rem] max-w-[16rem] animate-pulse">
                    <div style={{ width: '100%', aspectRatio: '4 / 3', borderRadius: '22px', background: 'rgba(251,249,217,0.14)' }} />
                    <div style={{ paddingTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ height: '16px', width: '80%', borderRadius: '999px', background: 'rgba(251,249,217,0.14)' }} />
                      <div style={{ height: '12px', width: '60%', borderRadius: '999px', background: 'rgba(251,249,217,0.14)' }} />
                    </div>
                  </div>
                ))
              : services.map((service) => {
                  const restaurantId = service.restaurant_id;
                  const label = getCateringServiceTypeLabel(service.service_type);
                  const { amount: price, perPerson } = getOfferStartingPrice(service);
                  const image = getImageUrl(service.image_url) || getImageUrl(service.restaurant_logo_url) || getImageUrl(service.restaurant_banner_url);

                  return (
                    <Link
                      key={`catering-${service.id}`}
                      to={`/catering-service-details/${restaurantId}`}
                      className="min-w-[16rem] max-w-[16rem] snap-start group focus:outline-none"
                      style={{ background: '#FCFBF5', color: '#051004', borderRadius: '22px', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 2px 10px rgba(5,16,4,0.08)' }}
                    >
                      <div style={{ position: 'relative' }}>
                        <img
                          src={image || namkeFallback}
                          alt={service.restaurant_name || label}
                          className="group-hover:scale-105 transition-transform duration-300"
                          style={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover' }}
                          loading="lazy"
                          onError={(e) => { e.target.src = namkeFallback; }}
                        />
                        <span style={{ position: 'absolute', top: '12px', left: '12px', background: '#FFC717', color: '#051004', fontFamily: 'Archivo, sans-serif', fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '5px 12px', borderRadius: '999px' }}>
                          {label}
                        </span>
                      </div>
                      <div style={{ padding: '16px 18px 18px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <h3 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '18px', lineHeight: 1.2 }} className="line-clamp-1">
                          {service.service_name || service.restaurant_name || t('catering.partnerCaterer')}
                        </h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'Archivo, sans-serif', fontSize: '13px', color: 'rgba(5,16,4,0.66)' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600, color: '#054625' }}>
                            <Star size={12} style={{ fill: '#FFC717', color: '#FFC717' }} />
                            {service.rating != null ? Number(service.rating).toFixed(1) : '4.5'}
                          </span>
                          <span style={{ color: 'rgba(5,16,4,0.3)' }}>·</span>
                          <span className="line-clamp-1">{service.event_type ? service.event_type : t('catering.events')}</span>
                        </div>
                        <p style={{ fontWeight: 600, fontSize: '16px', margin: '4px 0 0', color: '#051004' }}>
                          {t(perPerson ? 'catering.perPersonPrice' : 'catering.packagePrice', { price: formatPrice(price) })}
                        </p>
                      </div>
                    </Link>
                  );
                })}
          </div>

          <button
            type="button"
            onClick={() => scroll(-1)}
            disabled={!canScrollLeft}
            aria-label={t('home.previous')}
            className="hidden sm:flex absolute left-2 top-1/2 z-20 items-center justify-center w-10 h-10 rounded-full disabled:opacity-0 disabled:pointer-events-none hover:bg-[#FBF9D9] transition-colors"
            style={{ transform: 'translateY(-50%)', background: '#FCFBF5', color: '#054625', boxShadow: '0 6px 16px rgba(5,16,4,0.2)' }}
          >
            <ChevronLeft size={20} />
          </button>
          <button
            type="button"
            onClick={() => scroll(1)}
            disabled={!canScrollRight}
            aria-label={t('home.next')}
            className="hidden sm:flex absolute right-2 top-1/2 z-20 items-center justify-center w-10 h-10 rounded-full disabled:opacity-0 disabled:pointer-events-none hover:bg-[#FBF9D9] transition-colors"
            style={{ transform: 'translateY(-50%)', background: '#FCFBF5', color: '#054625', boxShadow: '0 6px 16px rgba(5,16,4,0.2)' }}
          >
            <ChevronRight size={20} />
          </button>
        </div>
        )}
      </div>
    </section>
  );
};

export default CateringServicesCarousel;
